/* eslint-disable react-refresh/only-export-components -- one typed provider/hook boundary keeps the route and registered renderer coupled to the same resolved snapshot contract. */
import React, { createContext, type ReactNode, useContext } from "react";

import type { PropertyRiskOverlay } from "@/api/propertyHazardReport";
import type { PropertyForestContext } from "@/api/propertyForestContext";

/**
 * Resolved, read-only map evidence consumed by a governed risk workspace.
 * Domain hooks fetch the data; the archetype owns publication and registered
 * map renderers own presentation. No route-level provider composition is needed.
 */
export interface FmRiskWorkspaceMapSnapshot {
  propertyId: string | null;
  context: PropertyForestContext | null;
  contextLoading: boolean;
  contextError: string | null;
  overlay: PropertyRiskOverlay | null;
  overlayLoading: boolean;
  overlayError: boolean;
  retry: () => void;
}

const RiskWorkspaceMapSnapshotContext =
  createContext<FmRiskWorkspaceMapSnapshot | null>(null);

export function FmRiskWorkspaceMapSnapshotProvider({
  snapshot,
  children,
}: {
  snapshot: FmRiskWorkspaceMapSnapshot;
  children: ReactNode;
}): React.ReactElement {
  return (
    <RiskWorkspaceMapSnapshotContext.Provider value={snapshot}>
      {children}
    </RiskWorkspaceMapSnapshotContext.Provider>
  );
}

export function useRiskWorkspaceMapSnapshot(
  propertyId: string | undefined,
): FmRiskWorkspaceMapSnapshot | null {
  const snapshot = useContext(RiskWorkspaceMapSnapshotContext);
  return snapshot?.propertyId === propertyId ? snapshot : null;
}
